(() => {
  if (window.RoamCarousel) return;
  const instances = new Map();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const getRoots = (scope) => [
    ...(scope.matches?.('[data-roam-carousel]') ? [scope] : []),
    ...scope.querySelectorAll('[data-roam-carousel]')
  ];
  class RoamCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.controls = root.querySelector('[data-carousel-controls]');
      if (!this.track || !this.controls) return;
      this.previous = root.querySelector('[data-carousel-previous]');
      this.next = root.querySelector('[data-carousel-next]');
      this.counter = root.querySelector('[data-carousel-count]');
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), {...options, passive: true});
      this.track.addEventListener('keydown', (event) => this.onKey(event), options);
      this.track.addEventListener('focusin', (event) => this.onFocus(event), options);
      this.previous.addEventListener('click', () => { this.update(); this.go(this.active - 1); }, options);
      this.next.addEventListener('click', () => { this.update(); this.go(this.active + 1); }, options);
      root.closest('details')?.addEventListener('toggle', () => this.scheduleUpdate(), options);
      this.observer = new ResizeObserver(() => this.scheduleUpdate());
      this.observer.observe(this.track);
      Array.from(this.track.children).forEach(card => this.observer.observe(card));
      this.root.classList.add('is-enhanced');
      this.update();
    }
    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }
    update() {
      this.cards = Array.from(this.track.children).filter(card => card.matches('article,li,a'));
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.max(0, this.track.scrollLeft);
      const overflow = this.cards.length > 1 && this.track.clientWidth > 0 && maximum > 2;
      this.controls.hidden = !overflow;
      this.previous.disabled = !overflow || position <= 2;
      this.next.disabled = !overflow || position >= maximum - 2;
      const trackBox = this.track.getBoundingClientRect();
      let nearest = 0;
      let distance = Infinity;
      this.cards.forEach((card, index) => {
        const offset = Math.abs(card.getBoundingClientRect().left - trackBox.left);
        if (offset < distance) { nearest = index; distance = offset; }
      });
      this.active = maximum > 2 && position >= maximum - 2 ? this.cards.length - 1 : nearest;
      const current = this.cards.length ? this.active + 1 : 0;
      const total = this.cards.length;
      this.counter.textContent = `${String(current).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      this.counter.setAttribute('aria-label', (this.counter.dataset.carouselCountTemplate || '')
        .replace('__CURRENT__', current).replace('__TOTAL__', total));
      const progress = maximum > 2 ? ((position / maximum) * .8 + .2) * 100 : 100;
      this.root.style.setProperty('--r-carousel-progress', `${Math.min(100, progress)}%`);
    }
    go(index, instant = false) {
      this.update();
      const boundedIndex = Math.min(Math.max(index, 0), this.cards.length - 1);
      const card = this.cards[boundedIndex];
      if (!card) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const offset = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({left: Math.min(Math.max(offset, 0), maximum), behavior: instant || reducedMotion() ? 'instant' : 'smooth'});
      this.scheduleUpdate();
    }
    onKey(event) {
      if (event.target !== this.track) return;
      this.update();
      const indices = {ArrowLeft: this.active - 1, ArrowRight: this.active + 1, Home: 0, End: this.cards.length - 1};
      if (!(event.key in indices)) return;
      event.preventDefault();
      this.go(indices[event.key]);
    }
    onFocus(event) {
      const card = this.cards.find(item => item.contains(event.target));
      if (!card) return;
      const cardBox = card.getBoundingClientRect();
      const trackBox = this.track.getBoundingClientRect();
      if (cardBox.left < trackBox.left - 2 || cardBox.right > trackBox.right + 2) this.go(this.cards.indexOf(card), true);
    }
    destroy() {
      this.controller.abort();
      this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.root.classList.remove('is-enhanced');
    }
  }
  function initialize(scope = document) {
    getRoots(scope).forEach(root => {
      if (!instances.has(root)) instances.set(root, new RoamCarousel(root));
    });
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    getRoots(event.target).forEach(root => { instances.get(root)?.destroy(); instances.delete(root); });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-roam-carousel]');
    const instance = instances.get(root);
    if (!instance) return;
    const details = root.closest('details');
    if (details) details.open = true;
    requestAnimationFrame(() => {
      instance.update();
      const index = instance.cards.findIndex(card => card === event.target || card.contains(event.target));
      if (index >= 0) instance.go(index, true);
    });
  });
  window.RoamCarousel = {initialize};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once: true});
  else initialize();
})();
